import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { EnvironmentProviders, Provider, enableProdMode, importProvidersFrom } from '@angular/core';
import { LanguageInterceptor } from '../i18n/language-interceptor.service';
import { LanguageService } from '../i18n/language.service';
import { DisclaimerInterceptor } from '../portal/disclaimer.interceptor';
import { IDisclaimerService } from '../portal/disclaimer.service';
import { IPortalComponentStateService } from '../portal/portal-component-state.service';
import { PortalEventService } from '../portal/portal-event.service';
import { IPortalNavigationService } from '../portal/portal-navigation.service';
import { IPortalPageTitleService } from '../portal/portal-page-title.service';
import { SearchQueryService } from '../portal/search-query.service';
import { UnsavedChangesGuard } from '../portal/unsaved-changes.guard';
import { HttpHeaderUtil } from '../utils/http-header-util';
import { LoggingService } from '../utils/logging.service';
import { IUploadFileSizeConfigService } from '../utils/upload-file-size-config-service';
import { BackendLocatorInterceptor } from './backend-locator.interceptor';
import { BackendLocatorService } from './backend-locator.service';
import { CustomHeadersInterceptor } from './custom-headers.interceptor';
import { EncodeUrlParamsSafelyInterceptor } from './encode-url-params-safely-interceptor';
import { EnvConfigParams, EnvironmentConfig } from './environment-config';
import { HttpActiveRequestService } from './http-active-request.service';
import { HttpErrorInterceptor } from './http-error.interceptor';
import { HttpProgressInterceptor } from './http-progress.interceptor';

let environmentConfig: EnvironmentConfig;

/**
 * Setzen der Umgebungsconfiguration. Diese wird dann unter dem Token EnvironmentConfig bereitgestellt.
 */
export function setEnvironmentConfig(envConfigParams: EnvConfigParams) {
    environmentConfig = new EnvironmentConfig(envConfigParams);
    if(environmentConfig.isProd()) {
        enableProdMode();
    }
}

/**
 * Die gemeinsamen Provider für alle UIs
 * (sowohl für das Portal, als auch für das Ausführen einzelner Microservice-UIs mit npm start).
 */
export const globalProviders: (Provider | EnvironmentProviders)[] = [
    /** Die EnvironemntConfig für andere Services zur Verfügung stellen. */
    {
        provide: EnvironmentConfig,
        useFactory: () => {
            // Factory, um Setzen der environmentConfig zu ermöglichen.
            // Fallback sind die Defaults für dev.
            return environmentConfig || new EnvironmentConfig();
        }
    },

    { provide: BackendLocatorService },

    /** Der Service, der den Suchstring aus dem Suchschlitz bereitstellt. */
    { provide: SearchQueryService },

    /** Stellt den Logger zur Verfügung. */
    { provide: LoggingService },

    /** Der Service für die Sprachumschaltung. */
    { provide: LanguageService },

    /** Util um Http-Header pro Request zu manipulieren **/
    { provide: HttpHeaderUtil },

    { provide: HttpActiveRequestService },

    { provide: PortalEventService },

    {
        provide: HTTP_INTERCEPTORS,
        useClass: CustomHeadersInterceptor,
        multi: true,
    },
    // Request URL so anpassen, dass abhängig
    // von der Umgebung bzw. dem "Entwicklungsmodus" (dev, prod) das passende Backend genutzt wird.
    // Siehe auch BackendLocatorService.
    {
        provide: HTTP_INTERCEPTORS,
        useClass: BackendLocatorInterceptor,
        multi: true,
    },
    {
        provide: HTTP_INTERCEPTORS,
        useClass: HttpProgressInterceptor,
        multi: true,
    },
    {
        provide: HTTP_INTERCEPTORS,
        useClass: LanguageInterceptor,
        multi: true,
    },
    {
        provide: HTTP_INTERCEPTORS,
        useClass: DisclaimerInterceptor,
        multi: true,
    },
    {
        provide: HTTP_INTERCEPTORS,
        useClass: HttpErrorInterceptor,
        multi: true,
    },
    {
        provide: HTTP_INTERCEPTORS,
        useClass: EncodeUrlParamsSafelyInterceptor,
        multi: true,
    },
    importProvidersFrom(HttpClientModule),
    { provide: UnsavedChangesGuard },
];

/**
 * Die folgenden Provider werden erst vom Portal-Service UI bereitgestellt.
 * Um die UIs einzelner Microservices mittels npm starten zu können, werden hier Leerimplementierungen
 * definiert. Diese sind jeweils wie folgt in das (nicht im Echtbetrieb verwendete) App-Module des Microservices aufzunehmen:
 *
 *   providers: [
 *       ...globalProviders,
 *       ...portalDummyProviders
 *   ]
 */
export const portalDummyProviders: any[] = [
    IPortalNavigationService,
    IPortalComponentStateService,
    IDisclaimerService,
    IPortalPageTitleService,
    IUploadFileSizeConfigService,
    UnsavedChangesGuard
];

